import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { productGroups } from "../../mock";
import { useLanguage } from "../../contexts/LanguageContext";
import CategorySidebar from "../layout/CategorySidebar";
import { ChevronRight, ChevronLeft, Search } from "lucide-react";
import ProductDetailPage from "./ProductDetailPage";
import { t } from "../../i18n";
import { getProductName, translateSubcategoryName } from "../../utils/translator";

const CategoryPage = () => {
    const params = useParams();
    const slug = params.slug || params.categorySlug;
    const subSlug = params.subSlug;
    const subSubSlug = params.subSubSlug;
    const { lang } = useLanguage();

    const [allProducts, setAllProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);

    // Reset pagination to first page when parameters change
    useEffect(() => {
        setCurrentPage(1);
    }, [slug, subSlug, subSubSlug]);

    useEffect(() => {
        setLoading(true);
        setError(false);
        if (slug) {
            fetch(`/data/products/${slug}.json`)
                .then((res) => {
                    if (!res.ok) {
                        throw new Error("Failed to load products");
                    }
                    return res.json();
                })
                .then((data) => {
                    setAllProducts(data);
                    setLoading(false);
                })
                .catch((err) => {
                    console.error("Error fetching products:", err);
                    setAllProducts([]);
                    setLoading(false);
                    setError(true);
                });
        } else {
            setAllProducts([]);
            setLoading(false);
        }
    }, [slug]);

    const normalize = (str) => {
        if (!str) return "";
        return str.toString()
            .replace(/İ/g, 'i')
            .replace(/I/g, 'i')
            .replace(/ı/g, 'i')
            .toLowerCase()
            .trim()
            .replace(/ğ/g, 'g')
            .replace(/ü/g, 'u')
            .replace(/ş/g, 's')
            .replace(/ö/g, 'o')
            .replace(/ç/g, 'c')
            .replace(/[^a-z0-9]/g, '');
    };

    // Find main category
    const mainCategory = productGroups.find((group) => group.slug === slug);
    
    if (loading) {
        return (
            <div className="pt-[140px] bg-[#f5f5f5] min-h-screen flex items-center justify-center">
                <div className="flex flex-col items-center space-y-4">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                    <p className="text-gray-500 text-sm tracking-widest uppercase font-bold animate-pulse">
                        {lang === "tr" ? "Ürünler Yükleniyor..." : "Loading Products..."}
                    </p>
                </div>
            </div>
        );
    }

    if (error || !mainCategory) {
        return (
            <div className="pt-[140px] bg-[#f5f5f5] min-h-screen flex flex-col items-center justify-center text-gray-900">
                <h1 className="text-2xl font-bold mb-4">
                    {lang === "tr" ? "Kategori Bulunamadı" : "Category Not Found"}
                </h1>
                <Link to="/products" className="text-primary hover:underline font-bold">
                    {lang === "tr" ? "Ürünlere Dön" : "Back to Products"}
                </Link>
            </div>
        );
    }
    
    // Check if any segment is a valid Product ID for this category
    const foundProduct = allProducts.find(p => 
        String(p.id) === String(subSlug) || 
        String(p.id) === String(subSubSlug)
    );
    
    if (foundProduct) {
        return <ProductDetailPage product={foundProduct} category={mainCategory} />;
    }

    // Determine current level and data
    let currentTitle = mainCategory[`title_${lang}`];
    let subCategories = [];
    let products = [];
    let breadcrumbs = [
        { title: t(lang, "nav.home"), path: "/" },
        { title: t(lang, "nav.products"), path: "/products" },
        { title: mainCategory[`title_${lang}`], path: `/products/${mainCategory.slug}` }
    ];

    if (subSlug) {
        // Look for a subcategory in mock.js that matches the current subSlug
        const subCat = mainCategory.subCategories?.find(sc => sc.slug === subSlug);
        
        if (subCat) {
            currentTitle = subCat[`title_${lang}`];
            breadcrumbs.push({ title: subCat[`title_${lang}`], path: `/products/${mainCategory.slug}/${subCat.slug}` });

            // NEW: Check if there are nested subcategories in mock.js for this subSlug
            const nestedSubCats = mainCategory.subCategories?.filter(sc => sc.parent === subSlug);
            
            // Get all unique subcategories from products for this subCat as fallback
            const normalizedSubCatTitle = normalize(subCat.title_tr);
            const subProducts = allProducts.filter(p => 
                normalize(p.series) === normalizedSubCatTitle || 
                normalize(p.subcategory) === normalizedSubCatTitle
            );
            
            const subSubCats = [...new Set(subProducts.map(p => p.subcategory))]
                .filter(ssc => ssc && normalize(ssc) !== normalizedSubCatTitle)
                .sort();
            
            if (subSubSlug) {
                // Try to find if subSubSlug is a nested subcategory from mock.js
                const subSubCat = nestedSubCats?.find(sc => sc.slug === subSubSlug);
                
                if (subSubCat) {
                    currentTitle = subSubCat[`title_${lang}`];
                    breadcrumbs.push({ title: currentTitle, path: `/products/${mainCategory.slug}/${subSlug}/${subSubSlug}` });
                    
                    const normalizedTitle = normalize(subSubCat.title_tr);
                    products = allProducts.filter(p => 
                        normalize(p.series) === normalizedTitle || 
                        normalize(p.subcategory) === normalizedTitle
                    );
                } else {
                    // Fallback to existing product-based sub-subcategory logic
                    const normalizedSubSubSlug = subSubSlug.toLowerCase();
                    const subSubTitle = subSubCats.find(ssc => 
                        ssc && ssc.toLowerCase().replace(/\s+/g, '-') === normalizedSubSubSlug
                    );
                    
                    if (subSubTitle) {
                        currentTitle = translateSubcategoryName(subSubTitle, lang);
                        breadcrumbs.push({ title: translateSubcategoryName(subSubTitle, lang), path: `/products/${mainCategory.slug}/${subCat.slug}/${subSubSlug}` });
                        products = subProducts.filter(p => p.subcategory === subSubTitle);
                    } else {
                        products = subProducts;
                    }
                }
            } else if (nestedSubCats && nestedSubCats.length > 0) {
                subCategories = nestedSubCats.map(nsc => ({
                    title: nsc[`title_${lang}`],
                    slug: nsc.slug,
                    imageUrl: nsc.imageUrl,
                    isNested: true,
                    parentSlug: subSlug // Pass the parent slug for hierarchy
                }));
            } else if (subSubCats.length > 0) {
                subCategories = subSubCats.map(ssc => {
                    const firstProduct = subProducts.find(p => p.subcategory === ssc);
                    return {
                        title: translateSubcategoryName(ssc, lang),
                        slug: ssc ? ssc.toLowerCase().replace(/\s+/g, '-') : 'unknown',
                        imageUrl: firstProduct ? firstProduct.imageUrl : null
                    };
                });
            } else {
                products = subProducts;
            }
        }
    } else {
        // Root level: Only show subcategories that have NO parent
        subCategories = mainCategory.subCategories?.filter(sc => !sc.parent) || [];
        if (subCategories.length === 0) {
            products = allProducts;
        }
    }

    const totalItems = products.length;
    const itemsPerPage = 24;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    
    // Slice products for current page
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentProducts = products.slice(startIndex, endIndex);

    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const getPageNumbers = () => {
        const pageNumbers = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        } else {
            pageNumbers.push(1);
            if (currentPage > 3) {
                pageNumbers.push("...");
            }
            const start = Math.max(2, currentPage - 1);
            const end = Math.min(totalPages - 1, currentPage + 1);
            for (let i = start; i <= end; i++) {
                pageNumbers.push(i);
            }
            if (currentPage < totalPages - 2) {
                pageNumbers.push("...");
            }
            pageNumbers.push(totalPages);
        }
        return pageNumbers;
    };

    return (
        <div className="pt-[115px] sm:pt-[125px] bg-[#f5f5f5] min-h-screen">
            <div className="bg-white border-b border-black/5 py-4 mb-6">
                <div className="w-full max-w-[1900px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center space-x-2 text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold">
                    {breadcrumbs.map((bc, index) => (
                        <React.Fragment key={index}>
                            <ChevronRight size={12} className="text-gray-400" />
                            {index === breadcrumbs.length - 1 ? (
                                <span className="text-primary">{bc.title}</span>
                            ) : (
                                <Link to={bc.path} className="hover:text-primary">
                                    {bc.title}
                                </Link>
                            )}
                        </React.Fragment>
                    ))}
                </div>
            </div>

            <div className="w-full max-w-[1900px] mx-auto px-4 sm:px-6 lg:px-8 pb-12">
                <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
                    <CategorySidebar />
                    <div className="flex-grow">
                        <div className="flex items-center justify-between mb-8 pb-3 border-b-2 border-primary/10">
                            <h1 className="text-xl font-bold tracking-widest text-gray-900 uppercase">
                                {currentTitle}
                            </h1>
                            <div className="bg-white px-4 py-1.5 rounded-full shadow-sm border border-gray-100 text-[10px] font-bold text-gray-400 tracking-widest uppercase">
                                {subCategories.length > 0 
                                    ? `${subCategories.length} ${lang === "tr" ? "ALT GRUP" : "SUBGROUPS"}`
                                    : `${products.length} ${lang === "tr" ? "ÜRÜN" : "PRODUCTS"}`
                                }
                            </div>
                        </div>

                        {subCategories.length > 0 ? (
                            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                                {subCategories.map((sc) => (
                                    <Link
                                        key={sc.slug}
                                        to={sc.isNested && sc.parentSlug
                                            ? `/products/${mainCategory.slug}/${sc.parentSlug}/${sc.slug}`
                                            : sc.isNested 
                                                ? `/products/${mainCategory.slug}/${sc.slug}`
                                                : `${window.location.pathname.replace(/\/$/, '')}/${sc.slug}`}
                                        className="group relative block aspect-square w-full overflow-hidden bg-white rounded-lg border border-gray-100 shadow-[0_3px_10px_rgb(0,0,0,0.06)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] transition-all duration-300"
                                    >
                                        {sc.imageUrl ? (
                                            <img 
                                                src={sc.imageUrl} 
                                                alt={sc.title} 
                                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center border-2 border-dashed border-gray-100 rounded-lg group-hover:border-primary/20 transition-colors">
                                                <span className="text-gray-300 text-xs font-medium tracking-widest">
                                                    {lang === "tr" ? "RESİM YOK" : "NO IMAGE"}
                                                </span>
                                            </div>
                                        )}
                                        <div className="absolute bottom-0 left-0 right-0 bg-black/60 backdrop-blur-[2px] py-3 text-center border-t border-white/10 transition-colors group-hover:bg-primary/90">
                                            <h3 className="text-[12px] font-bold tracking-widest text-white uppercase px-2 line-clamp-1">
                                                {sc[`title_${lang}`] || sc.title}
                                            </h3>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        ) : products.length > 0 ? (
                            <div>
                                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                                    {currentProducts.map((product) => (
                                        <Link
                                            key={product.id}
                                            to={`/products/${mainCategory.slug}/${subSlug ? subSlug + '/' : ''}${subSubSlug ? subSubSlug + '/' : ''}${product.id}`}
                                            className="group bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-500 overflow-hidden border border-gray-100 flex flex-col h-full"
                                        >
                                            <div className="h-56 bg-white flex items-center justify-center p-6 group-hover:bg-primary/5 transition-colors duration-500 relative">
                                                <img
                                                    src={product.imageUrl}
                                                    alt={getProductName(product, lang)}
                                                    className="max-w-full max-h-full object-contain transition-transform duration-700 group-hover:scale-110"
                                                />
                                                <div className="absolute top-4 right-4 bg-primary text-white text-[9px] font-bold px-2 py-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                                    #{product.id}
                                                </div>
                                            </div>
                                            <div className="p-6 bg-white border-t border-gray-50 flex-grow flex flex-col justify-between">
                                                <h3 className="text-[12px] font-bold text-gray-900 tracking-wide group-hover:text-primary transition-colors duration-300 mb-4 line-clamp-2 min-h-[3rem]">
                                                    {getProductName(product, lang)}
                                                </h3>
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center text-[10px] font-bold text-primary tracking-widest uppercase hover:underline decoration-2 underline-offset-4">
                                                        <span>{lang === "tr" ? "DETAYLAR" : "DETAILS"}</span>
                                                        <ChevronRight size={12} className="ml-1 transition-transform group-hover:translate-x-1" />
                                                    </div>
                                                </div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                                {totalPages > 1 && (
                                    <div className="flex items-center justify-center space-x-2 mt-12 pb-6">
                                        {/* Previous Button */}
                                        <button
                                            onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className={`flex items-center justify-center w-10 h-10 rounded-full border transition-all duration-300 ${
                                                currentPage === 1
                                                    ? "border-gray-200 text-gray-300 cursor-not-allowed"
                                                    : "border-gray-300 text-gray-700 hover:border-primary hover:text-white hover:bg-primary/90 hover:scale-105 active:scale-95"
                                            }`}
                                            title={lang === "tr" ? "Önceki Sayfa" : "Previous Page"}
                                        >
                                            <ChevronLeft size={16} />
                                        </button>

                                        {/* Page Numbers */}
                                        {getPageNumbers().map((num, idx) => {
                                            if (num === "...") {
                                                return (
                                                    <span
                                                        key={`ellipsis-${idx}`}
                                                        className="px-2 text-gray-400 font-medium select-none"
                                                    >
                                                        ...
                                                    </span>
                                                );
                                            }
                                            const isActive = num === currentPage;
                                            return (
                                                <button
                                                    key={`page-${num}`}
                                                    onClick={() => handlePageChange(num)}
                                                    className={`w-10 h-10 rounded-full text-xs font-bold tracking-wider transition-all duration-300 flex items-center justify-center ${
                                                        isActive
                                                            ? "bg-primary text-white shadow-md scale-105"
                                                            : "border border-gray-300 text-gray-700 hover:border-primary hover:text-white hover:bg-primary/95 hover:scale-105 active:scale-95"
                                                    }`}
                                                >
                                                    {num}
                                                </button>
                                            );
                                        })}

                                        {/* Next Button */}
                                        <button
                                            onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            className={`flex items-center justify-center w-10 h-10 rounded-full border transition-all duration-300 ${
                                                currentPage === totalPages
                                                    ? "border-gray-200 text-gray-300 cursor-not-allowed"
                                                    : "border-gray-300 text-gray-700 hover:border-primary hover:text-white hover:bg-primary/90 hover:scale-105 active:scale-95"
                                            }`}
                                            title={lang === "tr" ? "Sonraki Sayfa" : "Next Page"}
                                        >
                                            <ChevronRight size={16} />
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="bg-white rounded-2xl p-16 text-center shadow-sm border border-gray-100">
                                <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <Search className="text-gray-200 h-10 w-10" />
                                </div>
                                <h2 className="text-lg font-bold text-gray-400 uppercase tracking-widest mb-2">
                                    {lang === "tr" ? "Ürün Bulunamadı" : "No Products Found"}
                                </h2>
                                <p className="text-gray-300 text-sm italic">
                                    {lang === "tr" ? "Bu kategorideki ürünler yakında eklenecektir." : "Products in this category will be added soon."}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CategoryPage;
